import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const PlanFullscreen = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true); // Always dark mode for broadcast views
  const [broadcastStatus, setBroadcastStatus] = useState('idle');
  const autoScrollEnabled = true; // Always enabled
  const scrollSpeed = 30; // Fixed at 30 seconds for faster scrolling

  // Handle broadcast updates with useCallback to prevent re-render loops
  // Listen for broadcast changes from Firebase
  useEffect(() => {
    // Listen to all saved plans to detect broadcast changes
    const plansRef = collection(db, 'saved_plans');
    
    const unsubscribe = onSnapshot(plansRef, (snapshot) => {
      let activeBroadcast = null;
      
      snapshot.forEach((doc) => {
        const planData = doc.data();
        if (planData.isBroadcasting) {
          activeBroadcast = {
            id: doc.id,
            name: planData.name,
            planDate: planData.planDate,
            shift: planData.shift,
            data: planData.data,
            createdAt: planData.createdAt,
            broadcastAt: planData.broadcastAt,
            status: 'broadcasting'
          };
        }
      });
      
      if (activeBroadcast) {
        setPlan(activeBroadcast);
        setBroadcastStatus('broadcasting');
        setLoading(false);
        setError(null);
      } else {
        setPlan(null);
        setBroadcastStatus('idle');
        setLoading(false);
        setError('No active broadcast');
      }
    }, (error) => {
      console.error('Error listening to broadcast changes:', error);
      setBroadcastStatus('error');
      setError('Failed to connect to broadcast system');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Broadcast views always use dark mode for consistent formatting
  useEffect(() => {
    setIsDarkMode(true); // Always dark mode for broadcast views
  }, []);



  // Separate useEffect for auto-scroll to improve performance
  useEffect(() => {
    if (!autoScrollEnabled) return;
    
    let scrollAnimation = null;
    
    // Delay start to ensure DOM is fully loaded
    const startScroll = setTimeout(() => {
      const startTime = Date.now();
      
      const animateScroll = () => {
        const elapsed = Date.now() - startTime;
        const progress = (elapsed % (scrollSpeed * 1000)) / (scrollSpeed * 1000);
        
        // Find all horizontal scrollable containers (multiple rows)
        const scrollContainers = document.querySelectorAll('.flex.space-x-4.overflow-x-auto');
        
        if (scrollContainers.length > 0) {
          // Scroll all containers in perfect sync - no offset
          scrollContainers.forEach((container) => {
            const scrollWidth = container.scrollWidth;
            const clientWidth = container.clientWidth;
            const totalScrollDistance = scrollWidth - clientWidth;
            
            if (totalScrollDistance > 0) {
              // Create a smooth sine wave pattern - all rows scroll together
              const scrollPosition = Math.sin(progress * 2 * Math.PI) * totalScrollDistance / 2 + totalScrollDistance / 2;
              
              container.scrollTo({
                left: scrollPosition,
                behavior: 'auto' // Use auto for smooth continuous movement
              });
            }
          });
        }
        
        scrollAnimation = requestAnimationFrame(animateScroll);
      };
      
      scrollAnimation = requestAnimationFrame(animateScroll);
    }, 1000); // Wait 1 second for DOM to be ready
    
    return () => {
      clearTimeout(startScroll);
      if (scrollAnimation) {
        cancelAnimationFrame(scrollAnimation);
      }
    };
  }, [autoScrollEnabled, scrollSpeed]);



  // Helper function to get all assigned employees from the plan, grouped by login
  const getAllAssignedEmployees = () => {
    if (!plan || !plan.data) return [];
    
    const employeeMap = new Map(); // Map to group employees by login
    
    Object.keys(plan.data).forEach(floor => {
      Object.keys(plan.data[floor]).forEach(area => {
        plan.data[floor][area].forEach(employee => {
          if (employee && employee.login) {
            if (employeeMap.has(employee.login)) {
              // Employee already exists, add this role to their existing record
              const existingEmployee = employeeMap.get(employee.login);
              existingEmployee.roles.push({
                floor,
                area,
                role: employee.role || 'N/A'
              });
            } else {
              // New employee, create initial record
              employeeMap.set(employee.login, {
                ...employee,
                roles: [{
                  floor,
                  area,
                  role: employee.role || 'N/A'
                }]
              });
            }
          }
        });
      });
    });
    
    // Convert map to array and sort by login
    return Array.from(employeeMap.values()).sort((a, b) => a.login.localeCompare(b.login));
  };

  // Helper function to get area display name
  const getAreaDisplayName = (area, floor) => {
    // Special case: West Tote Runner becomes "West Runner" on floor A01
    if (area === 'westToteRunner' && floor === 'A01') {
      return 'West Runner';
    }
    
    const areaNames = {
      westSideWaterspider: 'West Spider',
      westToteRunner: 'West Tote Runner',
      westJamClear: 'West Jam Clear',
      westPA: 'West PA',
      eastSpider: 'East Spider',
      eastPA: 'East PA',
      eastRunner: 'East Runner',
      eastVRC: 'East VRC'
    };
    return areaNames[area] || area;
  };

  // Helper function to get floor display name
  const getFloorDisplayName = (floor) => {
    const floorNames = {
      A01: 'Floor A01',
      A02: 'Floor A02',
      A03: 'Floor A03',
      A04: 'Floor A04'
    };
    return floorNames[floor] || floor;
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-lg">Loading plan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'}`}>
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-2">Error Loading Plan</h1>
          <p className="text-lg text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'}`}>
        <div className="text-center">
          <div className="text-gray-500 text-6xl mb-4">📺</div>
          <h1 className="text-2xl font-bold mb-2">Universal Broadcast View</h1>
          <p className="text-lg text-gray-600">
            {broadcastStatus === 'idle' 
              ? 'Waiting for a plan to be pushed to broadcast from the main application...'
              : broadcastStatus === 'error'
                ? 'Connection error. Please check your network connection.'
                : 'No active broadcast at the moment.'
            }
          </p>
          <div className="mt-4 text-sm text-gray-500">
            <p>This view will automatically display any plan that is pushed to broadcast.</p>
            <p>Keep this tab open to receive broadcasts.</p>
          </div>
        </div>
      </div>
    );
  }

  const assignedEmployees = getAllAssignedEmployees();

    return (
    <div className={`h-screen flex flex-col ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'}`}>
      {/* Header */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'} border-b p-4 flex-shrink-0`}>
        <div className="w-full">
          <div className="grid grid-cols-2 gap-8">
            {/* Left Side - Plan Information - Always anchored to left */}
            <div className="text-left">
              <h1 className="text-4xl font-bold mb-2 text-white drop-shadow-2xl">{plan.name}</h1>
              <p className="text-xl text-gray-200 mb-1 drop-shadow-lg font-semibold">
                {plan.planDate} - {plan.shift} Shift
              </p>
              <p className="text-base text-gray-300 drop-shadow">
                Last updated: {plan.lastModified ? new Date(plan.lastModified).toLocaleString() : 'Unknown'}
              </p>
            </div>
            
            {/* Right Side - Staff Count and Controls - Always anchored to right */}
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-400">
                {assignedEmployees.length}
              </div>
              <div className="text-base text-gray-200 font-medium">Unique Staff</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Auto-fill viewport */}
      <div className="flex-1 overflow-hidden p-4 pb-2">
        {/* Auto-scroll Progress Indicator */}
        {autoScrollEnabled && assignedEmployees.length > 0 && (
          <div className="mb-4 text-center">
            <div className="inline-flex items-center space-x-2 bg-gray-800 rounded-lg px-3 py-2 border border-gray-600">
              <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-300 font-medium">
                Continuous Horizontal Scroll Active • {scrollSpeed < 60 ? `${scrollSpeed}s` : `${Math.round(scrollSpeed / 60)}min`} cycle
              </span>
            </div>
          </div>
        )}
        
        {assignedEmployees.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h2 className="text-3xl font-bold mb-3 text-white">No Staff Assigned</h2>
            <p className="text-xl text-gray-300">This plan doesn't have any staff assignments yet.</p>
          </div>
        ) : (
          <div className="flex flex-col space-y-4" style={{ 
            height: 'auto',
            paddingBottom: '20px'
          }}>
            {/* Fixed sizing for reliability - Always 3 rows */}
            {(() => {
              // Fixed approach - always use 3 rows for consistent layout
              const actualRows = 3;
              const cardsPerRow = Math.ceil(assignedEmployees.length / actualRows);
              
              const rows = [];
              for (let i = 0; i < actualRows; i++) {
                const startIndex = i * cardsPerRow;
                const endIndex = Math.min(startIndex + cardsPerRow, assignedEmployees.length);
                const rowEmployees = assignedEmployees.slice(startIndex, endIndex);
                
                rows.push(
                  <div key={i} className="flex-shrink-0 mb-4">
                    <div className="flex space-x-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                      {rowEmployees.map((employee, index) => (
                        <div 
                          key={`${employee.login}-${employee.floor}-${employee.area}`}
                          className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow duration-200 flex-shrink-0 flex`}
                          style={{ minWidth: '480px', maxWidth: '480px' }}
                        >
                          {/* Left Side - Staff Information */}
                          <div className="flex-1 pr-4 flex flex-col justify-center">
                            {/* Employee Info */}
                            <div className="text-center mb-4">
                              <div className="text-3xl font-bold mb-2 text-white drop-shadow-lg">{employee.login}</div>
                              {employee.role && employee.role !== 'N/A' && (
                                <div className="text-lg text-blue-300 font-semibold drop-shadow">{employee.role}</div>
                              )}
                            </div>
                          </div>

                          {/* Right Side - Assignment Information */}
                          <div className="flex-1 pl-4 border-l border-gray-600 flex flex-col justify-center">
                            <div className="text-center mb-4">
                              <div className="text-xl font-bold text-white mb-4 drop-shadow-lg">
                                Assignments
                              </div>
                            </div>
                            <div className="space-y-3">
                              {employee.roles.map((role, roleIndex) => (
                                <div key={roleIndex} className={`p-3 rounded border text-center ${isDarkMode ? 'bg-gray-700 border-gray-500' : 'bg-gray-100 border-gray-300'}`}>
                                  <div className="text-lg font-bold text-blue-300 mb-2 drop-shadow">
                                    {getFloorDisplayName(role.floor)}
                                  </div>
                                  <div className="text-base font-medium text-gray-200 drop-shadow">
                                    {getAreaDisplayName(role.area, role.floor)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return rows;
            })()}
          </div>
        )}
      </div>

      {/* Footer - Fixed to bottom */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'} border-t p-3 flex-shrink-0 relative z-10`}>
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-300">
          <p className="mb-2">
            Universal Broadcast View • {plan.name} • Showing {assignedEmployees.length} unique staff members
            {autoScrollEnabled && (
              <span className="ml-3 text-blue-400">
                🔄 Horizontal scrolling every {scrollSpeed < 60 ? `${scrollSpeed} seconds` : `${Math.round(scrollSpeed / 60)} minutes`}
              </span>
            )}
          </p>

        </div>
      </div>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(PlanFullscreen);
